<template>
 <MarketingShell>
 <div class="tm-integrated tm-dashboard">
  <div class="dash" :class="{ 'dash-dark': isDark }">
    <!-- Legacy Chart Banner -->
    <div v-if="!legacyBannerDismissed && false" class="legacy-banner-wrapper">
      <div class="legacy-banner" :class="{ 'legacy-banner-dark': isDark }">
        <div class="legacy-accent"></div>
        <div class="legacy-content">
          <div class="legacy-header">
            <span class="legacy-tag">Legacy</span>
            <span class="legacy-title">Old Chart Available</span>
          </div>
          <p class="legacy-desc">
            Current subscribers can access the legacy chart until their subscription expires. 
            <a href="https://chart.tradingcandle.com/#/apps/dashboard" target="_blank" rel="noopener">Open Old Chart →</a>
          </p>
          <p class="legacy-sub">Not accepting new users or subscriptions. Renewals on this app only.</p>
        </div>
        <button class="legacy-close" @click="dismissLegacyBanner" title="Dismiss" aria-label="Close">×</button>
      </div>
    </div>

    <!-- Nav -->
    <div class="tm-account-bar"><strong>Your account</strong><router-link to="/bots">Automated trading ↗</router-link><router-link v-if="isAdmin" to="/admin">Admin portal ↗</router-link><router-link to="/chart">Open workspace ↗</router-link></div>

    <!-- Loading -->
    <div v-if="loading" class="dash-loading">
      <div class="spinner"></div>
      <p>Loading your account…</p>
    </div>

    <!-- Main layout: sidebar tabs + content -->
    <div v-else class="dash-layout">
      <!-- Vertical Tab Menu -->
      <aside class="dash-sidebar">
        <div class="sidebar-user-brief">
          <div class="sidebar-avatar" v-if="user.imageUrl">
            <img :src="user.imageUrl" alt="Avatar" />
          </div>
          <div class="sidebar-avatar placeholder" v-else>
            {{ userInitial }}
          </div>
          <div class="sidebar-user-info">
            <span class="sidebar-user-name">{{ user.fullName || user.firstName || 'User' }}</span>
            <span class="sidebar-user-email">{{ user.email }}</span>
          </div>
        </div>

        <nav class="sidebar-tabs">
          <button
            v-for="tab in visibleTabs"
            :key="tab.id"
            class="sidebar-tab"
            :class="{ active: activeTab === tab.id }"
            @click="switchTab(tab.id)"
          >
            <span class="tab-icon" v-html="tab.icon"></span>
            <span class="tab-label">{{ tab.label }}</span>
            <span v-if="tab.badge" class="tab-badge" :class="tab.badgeClass">{{ tab.badge }}</span>
          </button>
        </nav>

        <div class="sidebar-footer">
          <button class="sidebar-tab logout-tab" @click="handleLogout">
            <span class="tab-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            </span>
            <span class="tab-label">Logout</span>
          </button>
        </div>
      </aside>

      <!-- Content Area -->
      <div class="dash-content">
        <SyfrusOffer @claimed="loadData" />
        <div class="content-header">
          <h1 class="content-title">{{ currentTabTitle }}</h1>
          <p class="content-subtitle">{{ currentTabSubtitle }}</p>
        </div>

        <!-- Overview Tab -->
        <div v-if="activeTab === 'overview'" class="tab-panel">

          <!-- Hero Welcome Banner -->
          <div class="ov-hero" :class="{ 'ov-hero-active': sub.active, 'ov-hero-inactive': !sub.active }">
            <div class="ov-hero-left">
              <div class="ov-hero-avatar">
                <img v-if="user.imageUrl" :src="user.imageUrl" alt="Avatar" />
                <span v-else class="ov-hero-initials">{{ userInitial }}</span>
              </div>
              <div class="ov-hero-text">
                <p class="ov-hero-greeting">Good day,</p>
                <h2 class="ov-hero-name">{{ user.fullName || user.firstName || 'Trader' }}</h2>
                <p class="ov-hero-email">{{ user.email }}</p>
              </div>
            </div>
            <div class="ov-hero-right">
              <div class="ov-status-pill" :class="sub.active ? 'pill-active' : 'pill-inactive'">
                <span class="ov-status-dot"></span>
                <span>{{ sub.active ? (sub.isTrial ? 'Trial Active' : 'Pro Active') : 'No Subscription' }}</span>
                <span v-if="sub.isTrial" class="ov-trial-chip">TRIAL</span>
              </div>
              <button v-if="sub.active" class="ov-hero-cta" @click="$router.push('/chart')">
                Open Charts
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
              </button>
              <button v-else class="ov-hero-cta ov-hero-cta-upgrade" @click="$router.push('/pricing')">
                View Plans
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
              </button>
            </div>
          </div>

          <!-- Stats Row -->
          <div class="ov-stats-grid">
            <!-- Subscription Stat -->
            <div class="ov-stat-card ov-stat-clickable" @click="switchTab('subscription')">
              <div class="ov-stat-icon ov-icon-sub">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>
              </div>
              <div class="ov-stat-body">
                <span class="ov-stat-label">Plan</span>
                <span class="ov-stat-val">{{ sub.active ? planLabel : 'None' }}</span>
              </div>
              <svg class="ov-stat-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
            </div>

            <!-- Days Remaining Stat -->
            <div class="ov-stat-card ov-stat-clickable" @click="switchTab('subscription')" v-if="sub.active">
              <div class="ov-stat-icon ov-icon-days" :class="{ 'ov-icon-warn': isExpiringSoon }">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              </div>
              <div class="ov-stat-body">
                <span class="ov-stat-label">Days Remaining</span>
                <span class="ov-stat-val" :class="{ 'ov-val-warn': isExpiringSoon }">{{ daysRemaining }}</span>
              </div>
              <svg class="ov-stat-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
            </div>

            <!-- AI Credits Stat -->
            <div class="ov-stat-card" v-if="aiCredits.monthlyAllocation > 0">
              <div class="ov-stat-icon ov-icon-ai">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
              </div>
              <div class="ov-stat-body">
                <span class="ov-stat-label">AI Credits Left</span>
                <span class="ov-stat-val ov-val-ai">{{ aiCredits.remaining }}<span class="ov-stat-of"> / {{ aiCredits.monthlyAllocation }}</span></span>
                <div class="ov-credits-mini-bar">
                  <div class="ov-credits-mini-fill" :style="{ width: (aiCredits.remaining / aiCredits.monthlyAllocation * 100) + '%' }"></div>
                </div>
              </div>
            </div>

            <!-- Last Payment Stat -->
            <div class="ov-stat-card ov-stat-clickable" v-if="payment.txnId" @click="switchTab('payment')">
              <div class="ov-stat-icon ov-icon-pay">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
              </div>
              <div class="ov-stat-body">
                <span class="ov-stat-label">Last Payment</span>
                <span class="ov-stat-val">₹{{ formatAmount(payment.amount) }}</span>
                <span class="ov-stat-sub">{{ formatDate(payment.paidAt) }}</span>
              </div>
              <svg class="ov-stat-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
            </div>

            <!-- Broker Status Stat -->
            <div class="ov-stat-card ov-stat-clickable" @click="switchTab('brokers')">
              <div class="ov-stat-icon" :class="(fyersConnected || upstoxConnected || aliceblueConnected || dhanConnected) ? 'ov-icon-broker-live' : 'ov-icon-broker'">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
              </div>
              <div class="ov-stat-body">
                <span class="ov-stat-label">Broker</span>
                <span class="ov-stat-val">{{ connectedBrokerName || 'Not Connected' }}</span>
                <span class="ov-stat-sub" v-if="connectedBrokerName">Live data active</span>
              </div>
              <svg class="ov-stat-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
            </div>
          </div>

          <!-- Quick Actions -->
          <div class="ov-section-header">
            <span class="ov-section-label">Quick Actions</span>
          </div>
          <div class="ov-quick-actions">
            <button class="ov-qa-btn" @click="$router.push('/chart')" v-if="sub.active">
              <div class="ov-qa-icon ov-qa-icon-chart">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3v18h18"/><path d="M7 16l4-8 4 4 6-10"/></svg>
              </div>
              <span class="ov-qa-label">Open Charts</span>
            </button>
            <button class="ov-qa-btn" @click="$router.push('/bots')">
              <div class="ov-qa-icon ov-qa-icon-bots">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="10" rx="2"/><circle cx="12" cy="5" r="2"/><path d="M12 7v4"/><line x1="8" y1="16" x2="8" y2="16"/><line x1="16" y1="16" x2="16" y2="16"/></svg>
              </div>
              <span class="ov-qa-label">Bots</span>
            </button>
            <button class="ov-qa-btn" @click="switchTab('brokers')">
              <div class="ov-qa-icon ov-qa-icon-broker">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
              </div>
              <span class="ov-qa-label">Connect Broker</span>
            </button>
            <button class="ov-qa-btn" @click="$router.push('/pricing')">
              <div class="ov-qa-icon ov-qa-icon-plan">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
              </div>
              <span class="ov-qa-label">{{ sub.active ? 'Manage Plan' : 'Subscribe' }}</span>
            </button>
            <button class="ov-qa-btn" @click="toggleTheme">
              <div class="ov-qa-icon ov-qa-icon-theme">
                <svg v-if="isDark" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
                <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
              </div>
              <span class="ov-qa-label">{{ isDark ? 'Light Mode' : 'Dark Mode' }}</span>
            </button>
          </div>

          <!-- Trial Box for new users without subscription -->
          <div v-if="!sub.active && !trial.taken" class="ov-trial-banner">
            <div class="ov-trial-glow"></div>
            <div class="ov-trial-left">
              <div class="ov-trial-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
              </div>
              <div>
                <h4 class="ov-trial-title">Start your free trial</h4>
                <p class="ov-trial-desc">7 days free · 200 AI credits · No credit card required</p>
              </div>
            </div>
            <button class="ov-trial-btn" :disabled="trialLoading" @click="activateTrial">
              <span v-if="trialLoading" class="spinner-sm"></span>
              <span v-else>Activate Trial</span>
            </button>
            <p v-if="trialError" class="trial-error" style="width:100%;margin-top:8px">{{ trialError }}</p>
          </div>

        </div>

        <!-- Subscription Tab -->
        <div v-if="activeTab === 'subscription'" class="tab-panel">
          <div class="card" :class="sub.active ? 'sub-active' : 'sub-inactive'">
            <div class="sub-header">
              <div class="sub-status-row">
                <span class="status-dot" :class="sub.active ? 'dot-green' : 'dot-red'"></span>
                <span class="status-label">{{ sub.active ? (sub.isTrial ? 'Trial Active' : 'Active') : 'Inactive' }}</span>
              </div>
              <span v-if="sub.active" class="plan-badge" :class="{ 'trial-badge-lg': sub.isTrial }">{{ planLabel }}</span>
            </div>

            <div v-if="sub.active" class="sub-details">
              <div class="detail-grid">
                <div class="detail-item">
                  <span class="detail-label">Plan</span>
                  <span class="detail-value">{{ planLabel }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Activated</span>
                  <span class="detail-value">{{ formatDate(sub.activatedAt) }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Expires</span>
                  <span class="detail-value" :class="{ 'expiring-soon': isExpiringSoon }">
                    {{ formatDate(sub.expiresAt) }}
                  </span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Days remaining</span>
                  <span class="detail-value">{{ daysRemaining }}</span>
                </div>
              </div>

              <!-- AI Credits section for active subscriptions -->
              <div class="ai-credits-section" v-if="aiCredits.monthlyAllocation > 0">
                <h4 class="credits-title">AI Credits</h4>
                <div class="credits-display">
                  <div class="credits-main">
                    <span class="credits-remaining">{{ aiCredits.remaining }}</span>
                    <span class="credits-total">/ {{ aiCredits.monthlyAllocation }}</span>
                    <span class="credits-label">remaining</span>
                  </div>
                  <div class="credits-bar-container">
                    <div class="credits-bar-bg">
                      <div class="credits-bar-fill" :style="{ width: (aiCredits.remaining / aiCredits.monthlyAllocation * 100) + '%' }"></div>
                    </div>
                    <span class="credits-percent">{{ Math.round(aiCredits.remaining / aiCredits.monthlyAllocation * 100) }}% available</span>
                  </div>
                </div>
              </div>

              <div class="sub-actions">
                <button class="btn-primary" @click="$router.push('/chart')">Open Charts →</button>
              </div>
            </div>

            <div v-else class="sub-empty">
              <p>You don't have an active subscription.</p>
              <button class="btn-primary" @click="$router.push('/pricing')">View Plans</button>
            </div>

            <!-- Trial Box for new users without subscription -->
            <div v-if="!sub.active && !trial.taken" class="trial-card">
              <div class="trial-card-content">
                <div class="trial-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                  </svg>
                </div>
                <div class="trial-info">
                  <h4>Start Free Trial</h4>
                  <p>7 days free • 200 AI credits • No credit card required</p>
                </div>
                <button
                  class="btn-trial"
                  :disabled="trialLoading"
                  @click="activateTrial"
                >
                  <span v-if="trialLoading" class="spinner-sm"></span>
                  <span v-else>Activate Trial</span>
                </button>
              </div>
              <p v-if="trialError" class="trial-error">{{ trialError }}</p>
            </div>
          </div>
        </div>

        <!-- Payment Tab -->
        <div v-if="activeTab === 'payment'" class="tab-panel">
          <div v-if="payment.txnId" class="card">
            <div class="detail-grid">
              <div class="detail-item">
                <span class="detail-label">Transaction ID</span>
                <span class="detail-value mono">{{ payment.txnId }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Amount</span>
                <span class="detail-value">₹{{ formatAmount(payment.amount) }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Payment Mode</span>
                <span class="detail-value">{{ payment.mode || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">PayU Ref</span>
                <span class="detail-value mono">{{ payment.mihpayid || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Bank</span>
                <span class="detail-value">{{ payment.bankcode || '—' }}</span>
              </div>
              <div class="detail-item">
                <span class="detail-label">Paid on</span>
                <span class="detail-value">{{ formatDate(payment.paidAt) }}</span>
              </div>
            </div>
          </div>
          <div v-else class="card empty-state">
            <p>No payment records found.</p>
          </div>
        </div>

        <!-- Quick Actions Tab -->
        <div v-if="activeTab === 'actions'" class="tab-panel">
          <div class="actions-row">
            <button class="action-card" @click="$router.push('/chart')" v-if="sub.active">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3v18h18"/><path d="M7 16l4-8 4 4 6-10"/></svg>
              <span>Open Charts</span>
            </button>
            <button class="action-card" @click="$router.push('/pricing')">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
              <span>{{ sub.active ? 'Manage Plan' : 'Subscribe' }}</span>
            </button>
            <button class="action-card" @click="toggleTheme">
              <svg v-if="isDark" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
              <svg v-else width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
              <span>{{ isDark ? 'Light Mode' : 'Dark Mode' }}</span>
            </button>
            <button class="action-card" @click="handleLogout">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
              <span>Logout</span>
            </button>
          </div>
        </div>

        <!-- Broker Integrations Tab -->
        <div v-if="activeTab === 'brokers'" class="tab-panel">
          <div class="card">
            <div class="broker-header">
              <h3 class="broker-title">Connect Your Broker</h3>
              <p class="broker-desc">Select a broker from the dropdown to connect for live market data. Only one broker can be active at a time.</p>
            </div>

            <!-- Broker Selector Dropdown -->
            <div class="broker-selector">
              <label class="broker-selector-label">Data Provider</label>
              <div class="broker-dropdown-wrapper">
                <div class="broker-dropdown" :class="{ open: brokerDropdownOpen }" @click="toggleBrokerDropdown">
                  <div class="broker-dropdown-selected">
                    <span v-if="selectedBroker" class="broker-dropdown-item-inner">
                      <span class="broker-dropdown-dot" :class="'dot-' + selectedBroker.id"></span>
                      <span class="broker-dropdown-name">{{ selectedBroker.name }}</span>
                      <span v-if="isBrokerConnected(selectedBroker.id)" class="broker-dropdown-status connected">Connected</span>
                      <span v-else-if="isBrokerExpired(selectedBroker.id)" class="broker-dropdown-status expired">Expired</span>
                    </span>
                    <span v-else class="broker-dropdown-placeholder">Select a broker...</span>
                    <svg class="broker-dropdown-arrow" :class="{ rotated: brokerDropdownOpen }" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M4.427 6.427a.75.75 0 011.06-.073L8 8.574l2.513-2.22a.75.75 0 11.994 1.124l-3 2.65a.75.75 0 01-.994 0l-3-2.65a.75.75 0 01-.086-1.06z"/>
                    </svg>
                  </div>
                  <transition name="dropdown-fade">
                    <div v-if="brokerDropdownOpen" class="broker-dropdown-menu" @click.stop>
                      <input
                        ref="brokerSearchInput"
                        v-model="brokerSearchQuery"
                        type="text"
                        class="broker-dropdown-search"
                        placeholder="Search brokers..."
                        @input="filterBrokers"
                      />
                      <div class="broker-dropdown-options">
                        <div
                          v-for="broker in filteredBrokers"
                          :key="broker.id"
                          class="broker-dropdown-option"
                          :class="{ active: selectedBroker && selectedBroker.id === broker.id, disabled: isIntegrationDisabled(broker.id) }"
                          @click="selectBroker(broker)"
                        >
                          <span class="broker-dropdown-dot" :class="'dot-' + broker.id"></span>
                          <span class="broker-dropdown-option-name">{{ broker.name }}</span>
                          <span class="broker-dropdown-option-desc">{{ broker.description }}</span>
                          <span v-if="isBrokerConnected(broker.id)" class="broker-dropdown-status connected">Connected</span>
                          <span v-else-if="isBrokerExpired(broker.id)" class="broker-dropdown-status expired">Expired</span>
                          <span v-else-if="isIntegrationDisabled(broker.id)" class="broker-dropdown-status disabled">Disabled</span>
                        </div>
                        <div v-if="filteredBrokers.length === 0" class="broker-dropdown-empty">
                          No brokers match your search
                        </div>
                      </div>
                    </div>
                  </transition>
                </div>
              </div>
            </div>

            <!-- Selected Broker Details -->
            <div v-if="selectedBroker" class="broker-detail-panel">
              <div class="broker-detail-header">
                <div class="broker-detail-info">
                  <span class="broker-detail-dot" :class="'dot-' + selectedBroker.id"></span>
                  <div>
                    <h4 class="broker-detail-name">{{ selectedBroker.name }}</h4>
                    <p class="broker-detail-features">{{ selectedBroker.features }}</p>
                  </div>
                </div>
                <span class="broker-detail-badge" :class="brokerStatusClass">{{ brokerStatusLabel }}</span>
              </div>

              <!-- Disabled Message -->
              <p v-if="isIntegrationDisabled(selectedBroker.id)" class="broker-disabled-msg">
                {{ getDisabledReason(selectedBroker.id) }}
              </p>

              <!-- Action Buttons -->
              <div class="broker-detail-actions">
                <!-- Not connected: Connect -->
                <button
                  v-if="!isBrokerConnected(selectedBroker.id) && !isBrokerExpired(selectedBroker.id) && !isIntegrationDisabled(selectedBroker.id)"
                  class="btn-broker"
                  :disabled="hasOtherBrokerConnected(selectedBroker.id)"
                  @click="openBrokerAuth(selectedBroker.id)"
                >
                  <span v-if="hasOtherBrokerConnected(selectedBroker.id)">Disconnect {{ connectedBrokerName }} first</span>
                  <span v-else>Connect {{ selectedBroker.name }}</span>
                </button>

                <!-- Expired: Reconnect -->
                <button
                  v-else-if="isBrokerExpired(selectedBroker.id)"
                  class="btn-broker btn-broker-reconnect"
                  @click="reconnectBroker(selectedBroker.id)"
                >Reconnect</button>

                <!-- Connected: Disconnect -->
                <button
                  v-if="isBrokerConnected(selectedBroker.id)"
                  class="btn-broker btn-broker-disconnect"
                  @click="disconnectBroker(selectedBroker.id)"
                >Disconnect</button>

                <!-- Manage Credentials -->
                <button
                  v-if="!isIntegrationDisabled(selectedBroker.id)"
                  class="btn-broker btn-broker-secondary"
                  @click="openBrokerAuth(selectedBroker.id)"
                >
                  {{ isBrokerConnected(selectedBroker.id) ? 'Manage Credentials' : 'Setup Guide' }}
                </button>
              </div>
            </div>

            <!-- No broker selected hint -->
            <div v-else class="broker-empty-state">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
              </svg>
              <p>Select a broker above to get started with live Indian market data</p>
            </div>
          </div>
        </div>

      </div>
    </div>

    <!-- Fyers Auth Modal -->
    <div v-if="showFyersAuth" class="modal-overlay" @click="closeBrokerAuth('fyers')">
      <div class="modal-content-dash" @click.stop>
        <div class="modal-header-dash">
          <h3>Connect Fyers Account</h3>
          <button @click="closeBrokerAuth('fyers')" class="close-btn">&times;</button>
        </div>
        <div class="modal-body-dash">
          <FyersAuth
            :credential-source="brokerCredentialSources.fyers"
            @authenticated="onFyersAuthenticated"
            @disconnected="onFyersDisconnected"
          />
        </div>
      </div>
    </div>

    <!-- Upstox Auth Modal -->
    <div v-if="showUpstoxAuth" class="modal-overlay" @click="closeBrokerAuth('upstox')">
      <div class="modal-content-dash" @click.stop>
        <div class="modal-header-dash">
          <h3>Connect Upstox Account</h3>
          <button @click="closeBrokerAuth('upstox')" class="close-btn">&times;</button>
        </div>
        <div class="modal-body-dash">
          <UpstoxAuth
            :credential-source="brokerCredentialSources.upstox"
            @authenticated="onUpstoxAuthenticated"
            @disconnected="onUpstoxDisconnected"
          />
        </div>
      </div>
    </div>

    <!-- AliceBlue Auth Modal -->
    <div v-if="showAliceBlueAuth" class="modal-overlay" @click="closeBrokerAuth('aliceblue')">
      <div class="modal-content-dash" @click.stop>
        <div class="modal-header-dash">
          <h3>Connect AliceBlue Account</h3>
          <button @click="closeBrokerAuth('aliceblue')" class="close-btn">&times;</button>
        </div>
        <div class="modal-body-dash">
          <AliceBlueAuth
            :credential-source="brokerCredentialSources.aliceblue"
            @authenticated="onAliceBlueAuthenticated"
            @disconnected="onAliceBlueDisconnected"
          />
        </div>
      </div>
    </div>

    <!-- DhanHQ Auth Modal -->
    <div v-if="showDhanAuth" class="modal-overlay" @click="closeBrokerAuth('dhan')">
      <div class="modal-content-dash" @click.stop>
        <div class="modal-header-dash">
          <h3>Connect DhanHQ Account</h3>
          <button @click="closeBrokerAuth('dhan')" class="close-btn">&times;</button>
        </div>
        <div class="modal-body-dash">
          <DhanAuth
            :credential-source="brokerCredentialSources.dhan"
            @authenticated="onDhanAuthenticated"
            @disconnected="onDhanDisconnected"
          />
        </div>
      </div>
    </div>

    <!-- Footer -->
    <footer class="dash-footer">© 2025 TradingCandle</footer>
  </div>

 </div>
 </MarketingShell>
</template>

<script>
import MarketingShell from "./marketing/MarketingShell.vue";
import SyfrusOffer from './SyfrusOffer.vue';
import FyersAuth from './FyersAuth.vue';
import FyersService from '../services/fyers_service.js';
import UpstoxAuth from './UpstoxAuth.vue';
import UpstoxService from '../services/upstox_service.js';
import AliceBlueAuth from './AliceBlueAuth.vue';
import AliceBlueService from '../services/aliceblue_service.js';
import DhanAuth from './DhanAuth.vue';
import DhanService from '../services/dhan_service.js';
import { resetBrokerReconnectDismissal } from '../components/BrokerReconnectPopup.vue';

export default {
  name: 'Dashboard',
  components: { MarketingShell,
    SyfrusOffer,
    FyersAuth,
    UpstoxAuth,
    AliceBlueAuth,
    DhanAuth
  },
  data() {
    return {
      loading: true,
      // Broker session expiry states
      fyersExpired: false,
      upstoxExpired: false,
      aliceblueExpired: false,
      user: { id: '', email: '', firstName: '', fullName: '', imageUrl: '' },
      sub: { active: false, plan: '', expiresAt: '', activatedAt: '', isTrial: false },
      payment: { txnId: '', amount: '', mode: '', mihpayid: '', bankcode: '', paidAt: '' },
      aiCredits: { remaining: 0, monthlyAllocation: 0, subscriptionType: '' },
      showFyersAuth: false,
      fyersConnected: false,
      showUpstoxAuth: false,
      upstoxConnected: false,
      showAliceBlueAuth: false,
      aliceblueConnected: false,
      showDhanAuth: false,
      dhanConnected: false,
      disabledIntegrations: {},
      brokerCredentialSources: { fyers: 'env', upstox: 'user', aliceblue: 'user', dhan: 'env' },
      // Broker dropdown
      brokerDropdownOpen: false,
      brokerSearchQuery: '',
      selectedBroker: null,
      brokerOptions: [
        { id: 'fyers', name: 'Fyers', description: 'NSE, BSE, F&O', features: 'NSE, BSE, F&O, Real-time WebSocket, Historical Data' },
        { id: 'upstox', name: 'Upstox', description: 'NSE, BSE, MCX, F&O', features: 'NSE, BSE, MCX, F&O, Real-time WebSocket, Historical Data' },
        { id: 'dhan', name: 'DhanHQ', description: 'NSE, BSE, MCX, F&O', features: 'NSE, BSE, MCX, F&O, Real-time WebSocket, Historical Data', hidden: true },
        { id: 'aliceblue', name: 'AliceBlue', description: 'NSE, BSE, F&O', features: 'NSE, BSE, F&O, Real-time WebSocket, Historical Data' },
      ],
      filteredBrokers: [],
      // Trial
      trial: { taken: false, active: false, expiresAt: null },
      trialLoading: false,
      trialError: null,
      // Legacy Banner
      legacyBannerDismissed: localStorage.getItem('tc_legacy_banner_dismissed') === 'true',
      // Admin
      isAdmin: false,
      // Profile menu state
      profileMenuOpen: false,
    }
  },
  computed: {
    isDark() {
      return this.$store.getters.isDarkTheme;
    },
    activeTab() {
      // Derive active tab from route path: /dashboard/brokers → 'brokers'
      const path = this.$route && this.$route.path || '';
      const segment = path.replace('/dashboard/', '').replace('/dashboard', '');
      const validTabs = ['overview', 'subscription', 'payment', 'actions', 'brokers'];
      // Map 'payments' path to 'payment' tab id
      if (segment === 'payments') return 'payment';
      if (validTabs.includes(segment)) return segment;
      return 'overview';
    },
    userInitial() {
      return (this.user.firstName || this.user.email || 'U').charAt(0).toUpperCase()
    },
    planLabel() {
      if (this.sub.isTrial || this.sub.plan === 'trial') return 'Free Trial'
      if (this.sub.plan === 'yearly') return 'Pro Yearly'
      if (this.sub.plan === 'monthly') return 'Pro Monthly'
      return this.sub.plan || 'Free'
    },
    daysRemaining() {
      if (!this.sub.expiresAt) return '—'
      const diff = new Date(this.sub.expiresAt) - new Date()
      if (diff <= 0) return 'Expired'
      return Math.ceil(diff / 86400000)
    },
    isExpiringSoon() {
      if (!this.sub.expiresAt) return false
      const diff = new Date(this.sub.expiresAt) - new Date()
      return diff > 0 && diff < 7 * 86400000
    },
    tabs() {
      return [
        {
          id: 'overview',
          label: 'Overview',
          icon: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>',
          title: 'Overview',
          subtitle: 'Your account, subscription and payment at a glance',
        },
        {
          id: 'subscription',
          label: 'Subscription',
          icon: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>',
          badge: this.sub.active ? 'Active' : 'Inactive',
          badgeClass: this.sub.active ? 'badge-green' : 'badge-muted',
          title: 'Subscription',
          subtitle: 'Manage your plan and billing',
        },
        {
          id: 'payment',
          label: 'Payments',
          icon: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>',
          visible: !!this.payment.txnId,
          title: 'Payments',
          subtitle: 'Your recent transaction history',
        },
        {
          id: 'actions',
          label: 'Quick Actions',
          icon: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>',
          title: 'Quick Actions',
          subtitle: 'Shortcuts to common tasks',
        },
        {
          id: 'brokers',
          label: 'Brokers',
          icon: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>',
          badge: (this.fyersConnected || this.upstoxConnected || this.aliceblueConnected || this.dhanConnected) ? 'Live' : null,
          badgeClass: 'badge-green',
          title: 'Broker Integrations',
          subtitle: 'Connect your trading accounts for live data',
        },
      ]
    },
    visibleTabs() {
      return this.tabs.filter(t => t.visible !== false)
    },
    currentTab() {
      return this.tabs.find(t => t.id === this.activeTab) || this.tabs[0]
    },
    currentTabTitle() {
      return this.currentTab.title
    },
    currentTabSubtitle() {
      return this.currentTab.subtitle
    },
    brokerStatusClass() {
      if (!this.selectedBroker) return '';
      if (this.isIntegrationDisabled(this.selectedBroker.id)) return 'disabled';
      if (this.isBrokerExpired(this.selectedBroker.id)) return 'expired';
      if (this.isBrokerConnected(this.selectedBroker.id)) return 'connected';
      return '';
    },
    brokerStatusLabel() {
      if (!this.selectedBroker) return '';
      if (this.isIntegrationDisabled(this.selectedBroker.id)) return 'Disabled';
      if (this.isBrokerExpired(this.selectedBroker.id)) return 'Session Expired';
      if (this.isBrokerConnected(this.selectedBroker.id)) return 'Connected';
      return 'Not Connected';
    },
    connectedBrokerName() {
      if (this.fyersConnected) return 'Fyers';
      if (this.upstoxConnected) return 'Upstox';
      if (this.aliceblueConnected) return 'AliceBlue';
      if (this.dhanConnected) return 'DhanHQ';
      return '';
    },
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  async mounted() {
    // Legacy: handle old ?tab= query param by redirecting to new path
    if (this.$route && this.$route.query && this.$route.query.tab) {
      const validTabs = ['overview', 'subscription', 'payment', 'actions', 'brokers'];
      if (validTabs.includes(this.$route.query.tab)) {
        const pathMap = { overview: 'overview', subscription: 'subscription', payment: 'payments', actions: 'actions', brokers: 'brokers' };
        this.$router.replace('/dashboard/' + (pathMap[this.$route.query.tab] || this.$route.query.tab));
      }
    }
    
    // Check broker sessions when brokers tab is active
    if (this.activeTab === 'brokers') {
      await this.checkBrokerSessions();
    }
    
    // Check broker sessions periodically
    setInterval(() => {
      if (this.activeTab === 'brokers') {
        this.checkBrokerSessions();
      }
    }, 30000); // Check every 30 seconds on brokers tab
    await this.loadData()
    this.fetchIntegrationStatus()
    this.checkFyersConnection()
    this.checkUpstoxConnection()
    this.checkAliceBlueConnection()
    this.checkDhanConnection()
    this.checkAdminRole()
    // Initialize broker dropdown
    this.filteredBrokers = this.brokerOptions;
    // Auto-select the connected broker if any
    this.$nextTick(() => {
      if (this.fyersConnected) this.selectedBroker = this.brokerOptions.find(b => b.id === 'fyers');
      else if (this.upstoxConnected) this.selectedBroker = this.brokerOptions.find(b => b.id === 'upstox');
      else if (this.aliceblueConnected) this.selectedBroker = this.brokerOptions.find(b => b.id === 'aliceblue');
      else if (this.dhanConnected) this.selectedBroker = this.brokerOptions.find(b => b.id === 'dhan');
    });
    // Add click outside listener for dropdown
    document.addEventListener('click', this.handleClickOutside)
  },
  methods: {
    switchTab(tabId) {
      // Map tab ids to URL paths
      const pathMap = { overview: 'overview', subscription: 'subscription', payment: 'payments', actions: 'actions', brokers: 'brokers' };
      const path = '/dashboard/' + (pathMap[tabId] || tabId);
      if (this.$route.path !== path) {
        this.$router.push(path);
      }
    },
    toggleTheme() {
      this.$store.dispatch('toggleTheme');
    },
    // Profile menu methods
    toggleProfileMenu() {
      this.profileMenuOpen = !this.profileMenuOpen;
    },
    handleClickOutside(event) {
      const dropdown = this.$el.querySelector('.user-profile-dropdown');
      if (dropdown && !dropdown.contains(event.target)) {
        this.profileMenuOpen = false;
      }
      // Close broker dropdown when clicking outside
      const brokerDropdown = this.$el.querySelector('.broker-dropdown-wrapper');
      if (brokerDropdown && !brokerDropdown.contains(event.target)) {
        this.brokerDropdownOpen = false;
      }
    },
    async checkBrokerSessions() {
      const brokers = [
        { name: 'fyers', service: FyersService, connected: this.fyersConnected },
        { name: 'upstox', service: UpstoxService, connected: this.upstoxConnected },
        { name: 'aliceblue', service: AliceBlueService, connected: this.aliceblueConnected },
        { name: 'dhan', service: DhanService, connected: this.dhanConnected }
      ];

      for (const broker of brokers) {
        if (broker.connected) {
          try {
            const validation = await broker.service.validateSession();
            const expiredKey = broker.name + 'Expired';
            this[expiredKey] = !validation.valid && validation.expired;
          } catch (error) {
            console.error(`[${broker.name}] Error checking session:`, error);
            const expiredKey = broker.name + 'Expired';
            this[expiredKey] = false;
          }
        }
      }
    },

    async openBrokerAuth(provider) {
      await this.fetchIntegrationStatus();
      this.setBrokerAuthVisible(provider, true);
    },

    closeBrokerAuth(provider) {
      this.setBrokerAuthVisible(provider, false);
    },

    setBrokerAuthVisible(provider, visible) {
      const modalKeys = {
        fyers: 'showFyersAuth',
        upstox: 'showUpstoxAuth',
        aliceblue: 'showAliceBlueAuth',
        dhan: 'showDhanAuth'
      };
      const key = modalKeys[provider];
      if (key) this[key] = visible;
    },

    async reconnectFyers() {
      await this.openBrokerAuth('fyers');
    },
    
    async reconnectUpstox() {
      await this.openBrokerAuth('upstox');
    },
    
    async reconnectAliceBlue() {
      await this.openBrokerAuth('aliceblue');
    },

    // Broker dropdown methods
    toggleBrokerDropdown() {
      this.brokerDropdownOpen = !this.brokerDropdownOpen;
      if (this.brokerDropdownOpen) {
        this.brokerSearchQuery = '';
        this.filteredBrokers = this.brokerOptions.filter(b => !b.hidden);
        this.$nextTick(() => {
          if (this.$refs.brokerSearchInput) {
            this.$refs.brokerSearchInput.focus();
          }
        });
      }
    },
    filterBrokers() {
      const q = this.brokerSearchQuery.toLowerCase().trim();
      if (!q) {
        this.filteredBrokers = this.brokerOptions.filter(b => !b.hidden);
        return;
      }
      this.filteredBrokers = this.brokerOptions.filter(b =>
        !b.hidden && (b.name.toLowerCase().includes(q) || b.description.toLowerCase().includes(q))
      );
    },
    selectBroker(broker) {
      if (this.isIntegrationDisabled(broker.id)) return;
      this.selectedBroker = broker;
      this.brokerDropdownOpen = false;
      this.brokerSearchQuery = '';
    },
    isBrokerConnected(id) {
      const map = { fyers: this.fyersConnected, upstox: this.upstoxConnected, aliceblue: this.aliceblueConnected, dhan: this.dhanConnected };
      return !!map[id];
    },
    isBrokerExpired(id) {
      const map = { fyers: this.fyersExpired, upstox: this.upstoxExpired, aliceblue: this.aliceblueExpired, dhan: this.dhanExpired };
      return !!map[id];
    },
    hasOtherBrokerConnected(id) {
      const others = ['fyers', 'upstox', 'aliceblue', 'dhan'].filter(b => b !== id);
      return others.some(b => this.isBrokerConnected(b));
    },
    reconnectBroker(id) {
      this.openBrokerAuth(id);
    },
    disconnectBroker(id) {
      const actions = {
        fyers: () => this.disconnectFyers(),
        upstox: () => this.disconnectUpstox(),
        aliceblue: () => this.disconnectAliceBlue(),
        dhan: () => this.disconnectDhan()
      };
      if (actions[id]) actions[id]();
    },
    
    handleMenuClick(action) {
      this.profileMenuOpen = false;
      switch (action) {
        case 'dashboard':
          // Already on dashboard
          break;
        case 'pricing':
          this.$router.push('/pricing');
          break;
        case 'chart':
          this.$router.push('/chart');
          break;
        case 'help':
          this.$router.push('/help');
          break;
        case 'download':
          this.$router.push('/download');
          break;
        case 'logout':
          this.handleLogout();
          break;
      }
    },
    getUserInitials() {
      if (this.$clerk.user && this.$clerk.user.firstName && this.$clerk.user.lastName) {
        return (this.$clerk.user.firstName[0] + this.$clerk.user.lastName[0]).toUpperCase();
      }
      if (this.$clerk.user && this.$clerk.user.firstName) {
        return this.$clerk.user.firstName[0].toUpperCase();
      }
      if (this.$clerk.user && this.$clerk.user.email) {
        return this.$clerk.user.email[0].toUpperCase();
      }
      return 'U';
    },
    dismissLegacyBanner() {
      this.legacyBannerDismissed = true;
      localStorage.setItem('tc_legacy_banner_dismissed', 'true');
    },
    checkFyersConnection() {
      this.fyersConnected = FyersService.isAuthenticated();
      if (!this.fyersConnected) {
        FyersService.restoreSessionFromServer().then(restored => {
          if (restored) this.fyersConnected = true;
        }).catch(() => {});
      }
    },
    checkUpstoxConnection() {
      this.upstoxConnected = UpstoxService.isAuthenticated();
      if (!this.upstoxConnected) {
        UpstoxService.restoreSessionFromServer().then(restored => {
          if (restored) this.upstoxConnected = true;
        }).catch(() => {});
      }
    },

    async checkAdminRole() {
      try {
        const metadata = await this.$clerk.getPublicMetadata();
        this.isAdmin = metadata.role === 'admin';
      } catch (error) {
        console.warn('[Dashboard] Failed to check admin role:', error);
        this.isAdmin = false;
      }
    },
    onFyersAuthenticated() {
      this.fyersConnected = true;
      this.fyersExpired = false;
      this.closeBrokerAuth('fyers');
      resetBrokerReconnectDismissal();
    },
    onFyersDisconnected() {
      this.fyersConnected = false;
    },
    disconnectFyers() {
      FyersService.disconnectFromServer();
      this.fyersConnected = false;
    },
    onUpstoxAuthenticated() {
      this.upstoxConnected = true;
      this.upstoxExpired = false;
      this.closeBrokerAuth('upstox');
    },
    onUpstoxDisconnected() {
      this.upstoxConnected = false;
    },
    disconnectUpstox() {
      UpstoxService.disconnectFromServer();
      this.upstoxConnected = false;
    },
    checkAliceBlueConnection() {
      this.aliceblueConnected = AliceBlueService.isAuthenticated();
      if (!this.aliceblueConnected) {
        AliceBlueService.restoreSessionFromServer().then(restored => {
          if (restored) this.aliceblueConnected = true;
        }).catch(() => {});
      }
    },
    checkDhanConnection() {
      this.dhanConnected = DhanService.isAuthenticated();
      if (!this.dhanConnected) {
        DhanService.restoreSessionFromServer().then(restored => {
          if (restored) this.dhanConnected = true;
        }).catch(() => {});
      }
    },
    onAliceBlueAuthenticated() {
      this.aliceblueConnected = true;
      this.aliceblueExpired = false;
      this.closeBrokerAuth('aliceblue');
    },
    onAliceBlueDisconnected() {
      this.aliceblueConnected = false;
    },
    onDhanAuthenticated() {
      this.dhanConnected = true;
      this.closeBrokerAuth('dhan');
    },
    onDhanDisconnected() {
      this.dhanConnected = false;
    },
    disconnectAliceBlue() {
      AliceBlueService.disconnectFromServer();
      this.aliceblueConnected = false;
    },
    disconnectDhan() {
      DhanService.disconnectFromServer();
      this.dhanConnected = false;
    },
    async fetchIntegrationStatus() {
      try {
        const response = await fetch(
          `${process.env.VUE_APP_API_BASE || window.location.origin}/api/integrations/status`
        );
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            this.disabledIntegrations = data.integrations || {};
            const sources = { ...this.brokerCredentialSources };
            Object.entries(data.integrations || {}).forEach(([provider, setting]) => {
              if (setting && setting.credentialSource) {
                sources[provider] = setting.credentialSource;
              }
            });
            this.brokerCredentialSources = sources;
          }
        }
      } catch (error) {
        console.warn('[Dashboard] Could not fetch integration status:', error.message);
      }
    },
    isIntegrationDisabled(provider) {
      const setting = this.disabledIntegrations[provider];
      return setting && !setting.enabled;
    },
    getDisabledReason(provider) {
      const setting = this.disabledIntegrations[provider];
      return setting && setting.disabledReason ? setting.disabledReason : 'Disabled by administrator';
    },
    async loadData() {
      this.loading = true
      try {
        if (this.$clerk.user) {
          this.user = { ...this.$clerk.user }
        }

        const token = await this.$clerk.getToken()
        if (token) {
          const res = await fetch(
            (process.env.VUE_APP_API_BASE || window.location.origin) + '/api/dashboard',
            { headers: { Authorization: `Bearer ${token}` } }
          )
          if (res.ok) {
            const data = await res.json()
            if (data.subscription) this.sub = data.subscription
            if (data.payment) this.payment = data.payment
            if (data.aiCredits) this.aiCredits = data.aiCredits

            if (data.subscription?.active) {
              this.$clerk.setSubscription(data.subscription.plan, data.subscription.expiresAt)
              this.$store.dispatch('activateSubscription', {
                plan: data.subscription.plan,
                expiresAt: data.subscription.expiresAt,
                internationalCharts: data.internationalCharts,
                indianMarketData: data.indianMarketData,
              })
            } else {
              this.$clerk.clearSubscription()
            }
          }
          // Check trial status for new users
          await this.checkTrialStatus()
        }
      } catch (err) {
        console.error('[Dashboard] Load error:', err)
      } finally {
        this.loading = false
      }
    },

    async checkTrialStatus() {
      try {
        const token = await this.$clerk.getToken()
        if (!token) return

        const baseUrl = process.env.VUE_APP_API_BASE || window.location.origin
        const res = await fetch(`${baseUrl}/api/trial/status`, {
          headers: { Authorization: `Bearer ${token}` },
        })

        if (res.ok) {
          const data = await res.json()
          this.trial = {
            taken: data.taken,
            active: data.active,
            expiresAt: data.expiresAt,
          }
        }
      } catch (err) {
        console.warn('[Dashboard] Failed to check trial status:', err)
      }
    },

    async activateTrial() {
      this.trialError = null
      this.trialLoading = true

      try {
        const token = await this.$clerk.getToken()
        if (!token) {
          this.trialError = 'Session expired. Please sign in again.'
          this.trialLoading = false
          return
        }

        const baseUrl = process.env.VUE_APP_API_BASE || window.location.origin
        const res = await fetch(`${baseUrl}/api/trial/activate`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        })

        const data = await res.json()

        if (!res.ok) {
          if (data.alreadyTaken) {
            this.trialError = 'You have already used your free trial.'
          } else if (data.hasSubscription) {
            this.trialError = 'You already have an active subscription.'
          } else {
            this.trialError = data.error || 'Failed to activate trial.'
          }
          this.trialLoading = false
          return
        }

        // Trial activated successfully
        this.trial = {
          taken: true,
          active: true,
          expiresAt: data.trial.expiresAt,
        }

        // Update subscription state
        this.sub = {
          active: true,
          plan: 'trial',
          expiresAt: data.trial.expiresAt,
          activatedAt: new Date().toISOString(),
          isTrial: true,
        }

        // Update Clerk state
        this.$clerk.setSubscription('trial', data.trial.expiresAt)
        this.$store.dispatch('activateSubscription', {
          plan: 'trial',
          expiresAt: data.trial.expiresAt,
          txnId: '',
        })

        // Refresh data to get AI credits
        await this.loadData()
      } catch (err) {
        console.error('[Dashboard] Trial activation error:', err)
        this.trialError = 'Something went wrong. Please try again.'
        this.trialLoading = false
      }
    },
    handleLogout() {
      this.$clerk.logout()
      this.$router.push('/')
    },
    formatDate(d) {
      if (!d) return '—'
      return new Date(d).toLocaleDateString('en-IN', {
        day: 'numeric', month: 'short', year: 'numeric',
      })
    },
    formatAmount(a) {
      if (!a) return '—'
      return parseFloat(a).toLocaleString('en-IN', { minimumFractionDigits: 2 })
    },
  },
}
</script>

<style scoped>
/* ── Base ── */
.dash {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--tc-bg-primary);
  color: var(--tc-text-primary);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

/* ── Legacy Banner ── */
.legacy-banner-wrapper {
  padding: 12px 48px;
  background: var(--tc-bg-primary, #fff);
  border-bottom: 1px solid var(--tc-border-secondary, #f0f3fa);
}

.legacy-banner {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 14px 18px;
  background: var(--tc-bg-elevated, #fff);
  border-radius: 10px;
  border: 1px solid var(--tc-border-primary, #e0e3eb);
  box-shadow: var(--tc-shadow-sm, 0 1px 3px rgba(0,0,0,0.08));
  position: relative;
  overflow: hidden;
}

.legacy-banner-dark {
  background: var(--tc-bg-elevated, #1a2035);
  border-color: var(--tc-border-primary, rgba(55, 59, 75, 0.6));
}

.legacy-accent {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: linear-gradient(180deg, #f59e0b 0%, #d97706 100%);
}

.legacy-content {
  flex: 1;
  min-width: 0;
  padding-left: 8px;
}

.legacy-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
}

.legacy-tag {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #92400e;
  background: #fef3c7;
  padding: 2px 8px;
  border-radius: 20px;
}

.legacy-banner-dark .legacy-tag {
  color: #fcd34d;
  background: rgba(251, 191, 36, 0.15);
}

.legacy-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--tc-text-primary, #131722);
}

.legacy-banner-dark .legacy-title {
  color: var(--tc-text-primary, #d1d4dc);
}

.legacy-desc {
  font-size: 13px;
  color: var(--tc-text-secondary, #787b86);
  line-height: 1.5;
  margin: 0 0 4px 0;
}

.legacy-desc a {
  color: var(--tc-accent, #2962ff);
  font-weight: 500;
  text-decoration: none;
}

.legacy-desc a:hover {
  text-decoration: underline;
}

.legacy-banner-dark .legacy-desc a {
  color: var(--tc-accent, #5b9cf6);
}

.legacy-sub {
  font-size: 12px;
  color: var(--tc-text-tertiary, #9ca3af);
  margin: 0;
  font-style: italic;
}

.legacy-banner-dark .legacy-sub {
  color: var(--tc-text-tertiary, #6b7280);
}

.legacy-close {
  background: none;
  border: none;
  font-size: 18px;
  line-height: 1;
  color: var(--tc-text-tertiary, #9ca3af);
  cursor: pointer;
  padding: 4px;
  margin: -4px -4px 0 0;
  border-radius: 6px;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
}

.legacy-close:hover {
  color: var(--tc-text-primary, #131722);
  background: var(--tc-bg-hover, rgba(0, 0, 0, 0.04));
}

.legacy-banner-dark .legacy-close:hover {
  color: var(--tc-text-primary, #d1d4dc);
  background: var(--tc-bg-hover, rgba(255, 255, 255, 0.06));
}

/* ── Nav ── */
.dash-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 48px;
  height: 56px;
  border-bottom: 1px solid var(--tc-nav-border);
  background: var(--tc-nav-bg);
  backdrop-filter: blur(8px);
  position: sticky;
  top: 0;
  z-index: 100;
}
.dash-logo {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  color: var(--tc-text-primary);
}
.dash-logo-text { font-family: 'DM Serif Display', serif; font-size: 18px; }
.dash-logo-img { height: 28px; width: auto; }
.dash-nav-links { display: flex; align-items: center; gap: 16px; }
.nav-link {
  font-size: 13px;
  font-weight: 500;
  color: var(--tc-text-secondary);
  cursor: pointer;
  text-decoration: none;
  transition: color 0.15s;
}
.nav-link:hover { color: var(--tc-text-primary); }
.nav-link.logout { color: var(--tc-red); }
.nav-link.logout:hover { color: var(--tc-red-hover); }

/* ── Loading ── */
.dash-loading {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
}
.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--tc-border-primary);
  border-top-color: var(--tc-green);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Layout: Sidebar + Content ── */
.dash-layout {
  flex: 1;
  display: flex;
  min-height: 0;
}

/* ── Sidebar ── */
.dash-sidebar {
  width: 260px;
  min-width: 260px;
  background: var(--tc-bg-elevated);
  border-right: 1px solid var(--tc-border-primary);
  display: flex;
  flex-direction: column;
  padding: 0;
  position: sticky;
  top: 56px;
  height: calc(100vh - 56px);
  overflow-y: auto;
}

.sidebar-user-brief {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 24px 20px 20px;
  border-bottom: 1px solid var(--tc-border-secondary);
}
.sidebar-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
}
.sidebar-avatar img { width: 100%; height: 100%; object-fit: cover; }
.sidebar-avatar.placeholder {
  background: #0f1923;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'DM Serif Display', serif;
  font-size: 16px;
}
.sidebar-user-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.sidebar-user-name {
  font-size: 14px;
  font-weight: 600;
  color: #0f1923;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sidebar-user-email {
  font-size: 11px;
  color: #8a9aaa;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Sidebar Tabs ── */
.sidebar-tabs {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 12px 12px 0;
  gap: 2px;
}
.sidebar-tab {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: #4a5568;
  text-align: left;
  width: 100%;
  position: relative;
}
.sidebar-tab:hover {
  background: #f5f4f0;
  color: #0f1923;
}
.sidebar-tab.active {
  background: #f0fdf4;
  color: #16a34a;
  font-weight: 600;
}
.sidebar-tab.active::before {
  content: '';
  position: absolute;
  left: 0;
  top: 6px;
  bottom: 6px;
  width: 3px;
  background: #16a34a;
  border-radius: 0 3px 3px 0;
}
.tab-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}
.sidebar-tab.active .tab-icon { color: #16a34a; }
.tab-label { flex: 1; }
.tab-badge {
  font-family: 'DM Mono', monospace;
  font-size: 10px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 10px;
  line-height: 1.4;
}
.badge-green {
  background: #dcfce7;
  color: #16a34a;
}
.badge-muted {
  background: #f1f1ef;
  color: #8a9aaa;
}

/* ── Sidebar Footer ── */
.sidebar-footer {
  padding: 12px;
  border-top: 1px solid #f0ede6;
}
.logout-tab {
  color: #dc2626 !important;
}
.logout-tab:hover {
  background: #fef2f2 !important;
  color: #b91c1c !important;
}

/* ── Content Area ── */
.dash-content {
  flex: 1;
  padding: 40px 48px 64px;
  min-width: 0;
  max-width: 900px;
}
.content-header {
  margin-bottom: 32px;
}
.content-title {
  font-family: 'DM Serif Display', serif;
  font-size: 28px;
  margin: 0 0 6px;
  color: #0f1923;
}
.content-subtitle {
  font-size: 14px;
  color: #8a9aaa;
  margin: 0;
}

/* ── Tab Panel ── */
.tab-panel {
  animation: fadeIn 0.2s ease;
}
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
}

/* ══════════════════════════════════════════
   NEW OVERVIEW REDESIGN
   ══════════════════════════════════════════ */

/* Hero Banner */
.ov-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 28px 32px;
  border-radius: 16px;
  margin-bottom: 20px;
  position: relative;
  overflow: hidden;
  border: 1px solid #e0e7d8;
  background: linear-gradient(135deg, #f4fbf4 0%, #edf7f0 50%, #f9fef9 100%);
  box-shadow: 0 2px 12px rgba(22, 163, 74, 0.06), 0 8px 32px rgba(0,0,0,0.04);
}
.ov-hero::before {
  content: '';
  position: absolute;
  top: -60px; right: -60px;
  width: 200px; height: 200px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(22,163,74,0.12) 0%, transparent 70%);
  pointer-events: none;
}
.ov-hero-inactive {
  background: linear-gradient(135deg, #fafaf8 0%, #f5f3ee 100%);
  border-color: #e8e5de;
}
.ov-hero-left {
  display: flex;
  align-items: center;
  gap: 18px;
}
.ov-hero-avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  overflow: hidden;
  background: #16a34a;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 2px solid rgba(22, 163, 74, 0.25);
  box-shadow: 0 2px 8px rgba(22, 163, 74, 0.2);
}
.ov-hero-avatar img { width: 100%; height: 100%; object-fit: cover; }
.ov-hero-initials {
  font-family: 'DM Serif Display', serif;
  font-size: 22px;
  color: #fff;
}
.ov-hero-text { display: flex; flex-direction: column; gap: 2px; }
.ov-hero-greeting {
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin: 0;
}
.ov-hero-name {
  font-family: 'DM Serif Display', serif;
  font-size: 24px;
  color: #0f1923;
  margin: 0;
  line-height: 1.2;
}
.ov-hero-email {
  font-size: 13px;
  color: #6b7280;
  margin: 0;
}
.ov-hero-right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}
.ov-status-pill {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.3px;
}
.pill-active {
  background: #dcfce7;
  color: #166534;
  border: 1px solid rgba(22, 163, 74, 0.2);
}
.pill-inactive {
  background: #f1f5f9;
  color: #64748b;
  border: 1px solid #e2e8f0;
}
.ov-status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
  opacity: 0.8;
}
.pill-active .ov-status-dot { background: #22c55e; opacity: 1; box-shadow: 0 0 4px rgba(34,197,94,0.5); }
.ov-trial-chip {
  background: #f5a623;
  color: #fff;
  font-size: 9px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 3px;
  letter-spacing: 0.5px;
}
.ov-hero-cta {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 18px;
  background: #16a34a;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(22, 163, 74, 0.3);
}
.ov-hero-cta:hover { background: #15803d; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(22, 163, 74, 0.4); }
.ov-hero-cta-upgrade { background: #0f1923; box-shadow: 0 2px 8px rgba(0,0,0,0.15); }
.ov-hero-cta-upgrade:hover { background: #1e293b; box-shadow: 0 4px 12px rgba(0,0,0,0.25); }

/* Stats Grid */
.ov-stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 14px;
  margin-bottom: 28px;
}
.ov-stat-card {
  background: #fff;
  border: 1px solid #e8e5de;
  border-radius: 12px;
  padding: 18px 20px;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
  transition: all 0.2s;
  position: relative;
}
.ov-stat-clickable { cursor: pointer; }
.ov-stat-clickable:hover {
  border-color: #c4ddca;
  box-shadow: 0 4px 14px rgba(0,0,0,0.08);
  transform: translateY(-1px);
}
.ov-stat-clickable:hover .ov-stat-arrow { opacity: 1; transform: translateX(2px); color: #16a34a; }
.ov-stat-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.ov-icon-sub { background: #f0fdf4; color: #16a34a; }
.ov-icon-days { background: #eff6ff; color: #2563eb; }
.ov-icon-warn { background: #fef3c7; color: #d97706; }
.ov-icon-ai { background: #fdf4ff; color: #9333ea; }
.ov-icon-pay { background: #fff7ed; color: #ea580c; }
.ov-icon-broker { background: #f8fafc; color: #64748b; }
.ov-icon-broker-live { background: #dcfce7; color: #16a34a; }
.ov-stat-body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}
.ov-stat-label {
  font-size: 11px;
  font-weight: 500;
  color: #8a9aaa;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.ov-stat-val {
  font-size: 16px;
  font-weight: 700;
  color: #0f1923;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ov-val-warn { color: #d97706 !important; }
.ov-val-ai { color: #9333ea !important; }
.ov-stat-of {
  font-size: 12px;
  font-weight: 500;
  color: #8a9aaa;
  margin-left: 2px;
}
.ov-stat-sub {
  font-size: 11px;
  color: #8a9aaa;
  margin-top: 2px;
}
.ov-credits-mini-bar {
  margin-top: 6px;
  height: 4px;
  background: #e8e5de;
  border-radius: 2px;
  overflow: hidden;
}
.ov-credits-mini-fill {
  height: 100%;
  background: linear-gradient(90deg, #9333ea, #a855f7);
  border-radius: 2px;
  transition: width 0.3s ease;
}
.ov-stat-arrow {
  opacity: 0;
  transition: all 0.2s;
  color: #8a9aaa;
  flex-shrink: 0;
  margin-top: 2px;
}

/* Section Header */
.ov-section-header {
  margin-bottom: 12px;
}
.ov-section-label {
  font-size: 11px;
  font-weight: 600;
  color: #8a9aaa;
  text-transform: uppercase;
  letter-spacing: 1px;
}

/* Quick Actions */
.ov-quick-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 28px;
}
.ov-qa-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  background: #fff;
  border: 1px solid #e8e5de;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
}
.ov-qa-btn:hover {
  border-color: #16a34a;
  background: #f0fdf4;
  color: #166534;
  transform: translateY(-1px);
  box-shadow: 0 3px 10px rgba(0,0,0,0.06);
}
.ov-qa-btn:hover .ov-qa-icon { transform: scale(1.05); }
.ov-qa-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s;
  flex-shrink: 0;
}
.ov-qa-icon-chart { background: #f0fdf4; color: #16a34a; }
.ov-qa-icon-bots { background: #eff6ff; color: #2563eb; }
.ov-qa-icon-broker { background: #fff7ed; color: #ea580c; }
.ov-qa-icon-plan { background: #fdf4ff; color: #9333ea; }
.ov-qa-icon-theme { background: #f8fafc; color: #64748b; }
.ov-qa-label { white-space: nowrap; }

/* Trial Banner */
.ov-trial-banner {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  padding: 22px 28px;
  background: linear-gradient(135deg, #0f1923 0%, #1a2a1e 100%);
  border-radius: 14px;
  position: relative;
  overflow: hidden;
  margin-top: 4px;
}
.ov-trial-glow {
  position: absolute;
  top: -40px; right: -40px;
  width: 160px; height: 160px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(34,197,94,0.2) 0%, transparent 70%);
  pointer-events: none;
}
.ov-trial-left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
}
.ov-trial-icon {
  width: 44px;
  height: 44px;
  background: rgba(34, 197, 94, 0.15);
  border: 1px solid rgba(34, 197, 94, 0.3);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #22c55e;
  flex-shrink: 0;
}
.ov-trial-title {
  font-size: 15px;
  font-weight: 600;
  color: #f1f5f9;
  margin: 0 0 4px;
}
.ov-trial-desc {
  font-size: 13px;
  color: #94a3b8;
  margin: 0;
}
.ov-trial-btn {
  padding: 10px 22px;
  background: #22c55e;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 130px;
  min-height: 40px;
  box-shadow: 0 2px 8px rgba(34, 197, 94, 0.3);
}
.ov-trial-btn:hover:not(:disabled) { background: #16a34a; transform: translateY(-1px); }
.ov-trial-btn:disabled { opacity: 0.6; cursor: not-allowed; }

/* ── Overview Grid (legacy, kept for other tabs) ── */
.overview-grid {
  display: flex;
  flex-direction: column;
  gap: 24px;
}
.overview-card {
  background: #fff;
  border: 1px solid #e8e5de;
  border-radius: 12px;
  padding: 28px 32px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 8px 24px rgba(0, 0, 0, 0.04);
}
.overview-card-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'DM Mono', monospace;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: #16a34a;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0ede6;
}
.overview-card-label svg {
  color: #16a34a;
  flex-shrink: 0;
}
.overview-card-arrow {
  margin-left: auto;
  font-size: 14px;
  color: #8a9aaa;
  transition: transform 0.15s, color 0.15s;
}
.overview-card.clickable {
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.overview-card.clickable:hover {
  border-color: #d0ccc4;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06), 0 8px 24px rgba(0, 0, 0, 0.06);
}
.overview-card.clickable:hover .overview-card-arrow {
  transform: translateX(3px);
  color: #16a34a;
}

/* ── Overview Summary Row ── */
.overview-summary-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 16px;
}
.overview-stat {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.overview-stat-label {
  font-size: 11px;
  color: #8a9aaa;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.overview-stat-value {
  font-size: 14px;
  color: #0f1923;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
}
.overview-stat-highlight {
  font-size: 20px;
  font-family: 'DM Serif Display', serif;
  color: #0f1923;
}
.status-dot-sm {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.avatar-sm {
  width: 44px;
  height: 44px;
}

/* ── Cards ── */
.card {
  background: #fff;
  border: 1px solid #e8e5de;
  border-radius: 12px;
  padding: 28px 32px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04), 0 8px 24px rgba(0, 0, 0, 0.04);
}
.sub-active { border-left: 4px solid #16a34a; }
.sub-inactive { border-left: 4px solid #d0ccc4; }

/* ── User Card ── */
.user-row { display: flex; align-items: center; gap: 20px; }
.avatar { width: 56px; height: 56px; border-radius: 50%; overflow: hidden; flex-shrink: 0; }
.avatar img { width: 100%; height: 100%; object-fit: cover; }
.avatar.placeholder {
  background: #0f1923;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'DM Serif Display', serif;
  font-size: 22px;
}
.user-name { font-family: 'DM Serif Display', serif; font-size: 22px; margin: 0 0 2px; }
.user-email { font-size: 14px; color: #4a5568; margin: 0; }
.user-id { font-family: 'DM Mono', monospace; font-size: 11px; color: #8a9aaa; margin: 4px 0 0; }

/* ── Subscription ── */
.sub-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; }
.sub-status-row { display: flex; align-items: center; gap: 8px; }
.status-dot { width: 10px; height: 10px; border-radius: 50%; }
.dot-green { background: #22c55e; box-shadow: 0 0 6px rgba(34, 197, 94, 0.4); }
.dot-red { background: #d0ccc4; }
.status-label { font-size: 14px; font-weight: 600; color: #0f1923; }
.plan-badge {
  font-family: 'DM Mono', monospace;
  font-size: 11px;
  font-weight: 500;
  background: #f0fdf4;
  color: #16a34a;
  padding: 4px 10px;
  border-radius: 4px;
  border: 1px solid rgba(22, 163, 74, 0.2);
}

/* ── Detail Grid ── */
.detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px 32px; }
.detail-item { display: flex; flex-direction: column; gap: 2px; }
.detail-label {
  font-size: 11px;
  color: #8a9aaa;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.detail-value { font-size: 14px; color: #0f1923; font-weight: 500; }
.detail-value.mono { font-family: 'DM Mono', monospace; font-size: 12px; }
.detail-value.expiring-soon { color: #dc2626; }

.sub-actions { margin-top: 24px; }
.sub-empty { text-align: center; padding: 12px 0; }
.sub-empty p { font-size: 14px; color: #4a5568; margin: 0 0 16px; }

/* ── Trial Card in Subscription Tab ── */
.trial-card {
  margin-top: 24px;
  padding: 20px 24px;
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border: 1px solid #bbf7d0;
  border-radius: 12px;
}
.trial-card-content {
  display: flex;
  align-items: center;
  gap: 16px;
}
.trial-icon {
  width: 48px;
  height: 48px;
  background: #16a34a;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  flex-shrink: 0;
}
.trial-info { flex: 1; }
.trial-info h4 {
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 600;
  color: #166534;
}
.trial-info p {
  margin: 0;
  font-size: 13px;
  color: #15803d;
}
.btn-trial {
  padding: 10px 20px;
  background: #16a34a;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 120px;
  min-height: 40px;
}
.btn-trial:hover:not(:disabled) {
  background: #15803d;
  transform: translateY(-1px);
}
.btn-trial:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.spinner-sm {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}
.trial-error {
  color: #dc2626;
  font-size: 13px;
  margin: 12px 0 0;
  text-align: center;
}
@media (max-width: 640px) {
  .trial-card-content {
    flex-direction: column;
    text-align: center;
  }
  .btn-trial { width: 100%; }
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #8a9aaa;
  font-size: 14px;
}

/* ── Buttons ── */
.btn-primary {
  padding: 10px 28px;
  background: #0f1923;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}
.btn-primary:hover { background: #16a34a; }

/* ── Quick Actions ── */
.actions-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
.action-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 24px 16px;
  background: #fff;
  border: 1px solid #e8e5de;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: #2b3a47;
}
.action-card:hover {
  border-color: #22c55e;
  background: #f0fdf4;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
}
.action-card svg { color: #4a5568; }
.action-card:hover svg { color: #16a34a; }

/* ── Broker Integrations ── */
.broker-header { margin-bottom: 20px; }
.broker-title { font-family: 'DM Serif Display', serif; font-size: 18px; margin: 0 0 4px; color: #0f1923; }
.broker-desc { font-size: 13px; color: #4a5568; margin: 0; }

/* Broker Selector Dropdown */
.broker-selector { margin-bottom: 24px; }
.broker-selector-label { display: block; font-size: 13px; font-weight: 600; color: #4a5568; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px; }
.broker-dropdown-wrapper { position: relative; }
.broker-dropdown { position: relative; cursor: pointer; }
.broker-dropdown-selected {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: #fff;
  border: 2px solid #e8e5de;
  border-radius: 10px;
  transition: all 0.2s;
}
.broker-dropdown.open .broker-dropdown-selected { border-color: #f5a623; box-shadow: 0 0 0 3px rgba(245, 166, 35, 0.1); }
.broker-dropdown-selected:hover { border-color: #d0ccc4; }
.broker-dropdown-item-inner { display: flex; align-items: center; gap: 10px; flex: 1; }
.broker-dropdown-placeholder { color: #9ca3af; font-size: 14px; flex: 1; }
.broker-dropdown-name { font-size: 14px; font-weight: 600; color: #0f1923; }
.broker-dropdown-arrow { color: #6b7280; transition: transform 0.2s; margin-left: auto; }
.broker-dropdown-arrow.rotated { transform: rotate(180deg); }
.broker-dropdown-status { font-size: 11px; font-weight: 500; padding: 2px 8px; border-radius: 4px; }
.broker-dropdown-status.connected { background: #dcfce7; color: #16a34a; }
.broker-dropdown-status.expired { background: #fef3c7; color: #d97706; }
.broker-dropdown-status.disabled { background: #fef2f2; color: #dc2626; }
.broker-dropdown-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #e8e5de;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  z-index: 100;
  overflow: hidden;
}
.broker-dropdown-search {
  width: 100%;
  padding: 12px 16px;
  border: none;
  border-bottom: 1px solid #e8e5de;
  font-size: 14px;
  outline: none;
  box-sizing: border-box;
  background: #fafaf8;
}
.broker-dropdown-search::placeholder { color: #9ca3af; }
.broker-dropdown-options { max-height: 200px; overflow-y: auto; }
.broker-dropdown-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.15s;
}
.broker-dropdown-option:hover { background: #f7f7f5; }
.broker-dropdown-option.active { background: #fef9f0; }
.broker-dropdown-option.disabled { opacity: 0.5; cursor: not-allowed; }
.broker-dropdown-option-name { font-size: 14px; font-weight: 600; color: #0f1923; }
.broker-dropdown-option-desc { font-size: 12px; color: #6b7280; flex: 1; }
.broker-dropdown-empty { padding: 16px; text-align: center; color: #9ca3af; font-size: 13px; }
.broker-dropdown-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}
.broker-dropdown-dot.dot-fyers { background: #1a73e8; }
.broker-dropdown-dot.dot-upstox { background: #5B2D8E; }
.broker-dropdown-dot.dot-aliceblue { background: #2563EB; }

/* Broker Detail Panel */
.broker-detail-panel {
  padding: 20px;
  background: #fafaf8;
  border: 1px solid #e8e5de;
  border-radius: 10px;
}
.broker-detail-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
.broker-detail-info { display: flex; align-items: center; gap: 12px; }
.broker-detail-dot { width: 12px; height: 12px; border-radius: 50%; flex-shrink: 0; }
.broker-detail-dot.dot-fyers { background: #1a73e8; }
.broker-detail-dot.dot-upstox { background: #5B2D8E; }
.broker-detail-dot.dot-aliceblue { background: #2563EB; }
.broker-detail-name { font-size: 16px; font-weight: 600; color: #0f1923; margin: 0; }
.broker-detail-features { font-size: 12px; color: #6b7280; margin: 4px 0 0; }
.broker-detail-badge {
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 6px;
  background: #f1f1ef;
  color: #8a9aaa;
}
.broker-detail-badge.connected { background: #dcfce7; color: #16a34a; }
.broker-detail-badge.expired { background: #fef3c7; color: #d97706; }
.broker-detail-badge.disabled { background: #fef2f2; color: #dc2626; }
.broker-detail-actions { display: flex; gap: 10px; flex-wrap: wrap; }
.broker-disabled-msg { font-size: 12px; color: #dc2626; margin: 0 0 12px; font-style: italic; }

/* Broker Empty State */
.broker-empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #9ca3af;
}
.broker-empty-state svg { margin-bottom: 12px; color: #d0ccc4; }
.broker-empty-state p { font-size: 14px; margin: 0; }

/* Broker Buttons */
.btn-broker {
  padding: 8px 20px;
  background: #f5a623;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}
.btn-broker:hover { background: #e09500; }
.btn-broker:disabled { background: #9ca3af; cursor: not-allowed; opacity: 0.7; }
.btn-broker-secondary { background: transparent; color: #4a5568; border: 1px solid #e8e5de; }
.btn-broker-secondary:hover { background: #f7f7f5; border-color: #d0ccc4; color: #0f1923; }
.btn-broker-disconnect { background: #dc2626; }
.btn-broker-disconnect:hover { background: #b91c1c; color: white; }
.btn-broker-reconnect {
  background: #f59e0b;
  color: white;
  border: 1px solid #f59e0b;
}
.btn-broker-reconnect:hover {
  background: #d97706;
  border-color: #d97706;
}

/* Dropdown transition */
.dropdown-fade-enter-active, .dropdown-fade-leave-active { transition: opacity 0.15s, transform 0.15s; }
.dropdown-fade-enter, .dropdown-fade-leave-to { opacity: 0; transform: translateY(-4px); }

/* ── Dashboard Modals ── */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.modal-content-dash {
  background: var(--tc-bg-primary);
  border-radius: 12px;
  max-width: 560px;
  width: 92%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
}
.modal-header-dash {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid var(--tc-border-primary);
}
.modal-header-dash h3 { margin: 0; font-family: 'DM Serif Display', serif; font-size: 18px; color: var(--tc-text-primary); }
.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: var(--tc-text-secondary);
  line-height: 1;
}
.close-btn:hover { color: var(--tc-text-primary); }
.modal-body-dash { padding: 24px; }

/* ── AI Settings ── */
.ai-active { border-left: 4px solid #22c55e; }
.ai-inactive { border-left: 4px solid #d0ccc4; }

.ai-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.ai-status-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.dot-green { background: #22c55e; box-shadow: 0 0 6px rgba(34, 197, 94, 0.4); }
.dot-red { background: #d0ccc4; }

.status-label {
  font-size: 14px;
  font-weight: 600;
  color: #0f1923;
}

.ai-badge {
  font-family: 'DM Mono', monospace;
  font-size: 11px;
  font-weight: 500;
  padding: 4px 10px;
  border-radius: 4px;
  border: 1px solid;
}

.ai-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.ai-description {
  font-size: 14px;
  color: #4a5568;
  line-height: 1.6;
}

.ai-description p {
  margin: 0 0 12px;
}

.ai-features {
  margin: 0;
  padding-left: 20px;
  font-size: 13px;
  color: #4a5568;
}

.ai-features li {
  margin: 6px 0;
}

.ai-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-label {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
}

.label-text {
  font-size: 14px;
  font-weight: 500;
  color: #0f1923;
}

.form-label-text {
  font-size: 13px;
  font-weight: 500;
  color: #4a5568;
}

/* Toggle Switch */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: .3s;
  border-radius: 24px;
}

.toggle-slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: .3s;
  border-radius: 50%;
}

input:checked + .toggle-slider {
  background-color: #22c55e;
}

input:checked + .toggle-slider:before {
  transform: translateX(20px);
}

/* Input Group */
.input-group {
  display: flex;
  gap: 8px;
}

.form-input {
  flex: 1;
  padding: 10px 12px;
  border: 1px solid #e8e5de;
  border-radius: 6px;
  font-size: 13px;
  outline: none;
  transition: border-color 0.15s;
}

.form-input:focus {
  border-color: #22c55e;
}

.input-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid #e8e5de;
  border-radius: 6px;
  background: white;
  cursor: pointer;
  color: #4a5568;
  transition: all 0.15s;
}

.input-btn:hover {
  border-color: #22c55e;
  color: #22c55e;
}

.form-hint {
  font-size: 12px;
  color: #8a9aaa;
  margin: 0;
}

/* AI Actions */
.ai-actions {
  display: flex;
  gap: 12px;
}

.btn-secondary {
  padding: 10px 20px;
  background: transparent;
  border: 1px solid #e8e5de;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  color: #4a5568;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-secondary:hover {
  border-color: #dc2626;
  color: #dc2626;
}

.ai-info {
  background: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 6px;
  padding: 12px 16px;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #166534;
}

.info-item svg {
  color: #22c55e;
}

/* AI Usage Card */
.ai-usage-card {
  margin-top: 16px;
}

.usage-title {
  font-size: 14px;
  font-weight: 600;
  color: #0f1923;
  margin: 0 0 16px;
}

.usage-steps {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.usage-step {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 13px;
  color: #4a5568;
}

.step-number {
  width: 24px;
  height: 24px;
  background: #0f1923;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
}

/* ── Footer ── */
.dash-footer {
  text-align: center;
  padding: 24px;
  font-size: 11px;
  color: #8a9aaa;
  border-top: 1px solid #e8e5de;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* ── Responsive ── */
@media (max-width: 768px) {
  .dash-layout {
    flex-direction: column;
  }
  .dash-sidebar {
    width: 100%;
    min-width: 100%;
    height: auto;
    position: relative;
    top: 0;
    border-right: none;
    border-bottom: 1px solid #e8e5de;
  }
  .sidebar-user-brief {
    padding: 16px 20px;
  }
  .sidebar-tabs {
    flex-direction: row;
    overflow-x: auto;
    padding: 8px 12px;
    gap: 4px;
    -webkit-overflow-scrolling: touch;
  }
  .sidebar-tab {
    white-space: nowrap;
    padding: 8px 14px;
    flex-shrink: 0;
  }
  .sidebar-tab.active::before {
    left: 6px;
    right: 6px;
    top: auto;
    bottom: 0;
    width: auto;
    height: 3px;
    border-radius: 3px 3px 0 0;
  }
  .sidebar-footer {
    display: none;
  }
  .dash-content {
    padding: 24px 20px 48px;
  }
  .content-title {
    font-size: 22px;
  }
  .dash-nav { padding: 0 20px; }
  .card { padding: 20px; }
  .detail-grid { grid-template-columns: 1fr; }
  .actions-row { grid-template-columns: 1fr; }
  .user-row { flex-direction: column; text-align: center; }
  /* New overview mobile */
  .ov-hero { flex-direction: column; align-items: flex-start; padding: 20px; }
  .ov-hero-right { width: 100%; justify-content: flex-start; }
  .ov-stats-grid { grid-template-columns: 1fr 1fr; }
  .ov-quick-actions { gap: 8px; }
  .ov-qa-btn { flex: 1; min-width: 120px; }
  .ov-trial-banner { padding: 18px 20px; }
  .ov-trial-left { flex-direction: column; align-items: flex-start; }
  .ov-trial-btn { width: 100%; }
}

/* ══════════════════════════════════════════
   DARK THEME
   ══════════════════════════════════════════ */
.dash-dark {
  background: #0c1018;
  color: #d1d4dc;
}
.dash-dark .dash-nav {
  background: rgba(12, 16, 24, 0.95);
  border-bottom-color: rgba(55, 59, 75, 0.5);
}
.dash-dark .dash-logo { color: #d1d4dc; }
.dash-dark .nav-link { color: #9ca3af; }
.dash-dark .nav-link:hover { color: #e5e7eb; }
.dash-dark .nav-link.logout { color: #f87171; }
.dash-dark .nav-link.logout:hover { color: #ef4444; }
.dash-dark .dash-sidebar {
  background: #0f1520;
  border-right-color: rgba(55, 59, 75, 0.5);
}
.dash-dark .sidebar-user-brief { border-bottom-color: rgba(55, 59, 75, 0.4); }
.dash-dark .sidebar-user-name { color: #e5e7eb; }
.dash-dark .sidebar-user-email { color: #787b86; }
.dash-dark .sidebar-avatar.placeholder { background: #22c55e; }
.dash-dark .sidebar-tab { color: #9ca3af; }
.dash-dark .sidebar-tab:hover { background: #1a2035; color: #e5e7eb; }
.dash-dark .sidebar-tab.active { background: rgba(34,197,94,0.1); color: #22c55e; }
.dash-dark .sidebar-footer { border-top-color: rgba(55, 59, 75, 0.4); }
.dash-dark .logout-tab { color: #f87171 !important; }
.dash-dark .logout-tab:hover { background: rgba(239,68,68,0.1) !important; color: #ef4444 !important; }
.dash-dark .content-title { color: #e5e7eb; }
.dash-dark .content-subtitle { color: #787b86; }
.dash-dark .overview-card,
.dash-dark .card {
  background: #121826;
  border-color: rgba(55, 59, 75, 0.5);
  box-shadow: 0 1px 3px rgba(0,0,0,0.2), 0 8px 24px rgba(0,0,0,0.2);
}
.dash-dark .overview-card-label { border-bottom-color: rgba(55, 59, 75, 0.4); }
.dash-dark .overview-card-arrow { color: #787b86; }
.dash-dark .overview-card.clickable:hover {
  border-color: rgba(55, 59, 75, 0.8);
  box-shadow: 0 2px 8px rgba(0,0,0,0.3), 0 8px 24px rgba(0,0,0,0.3);
}
.dash-dark .overview-stat-value { color: #e5e7eb; }
.dash-dark .overview-stat-highlight { color: #e5e7eb; }
.dash-dark .overview-stat-label { color: #787b86; }
.dash-dark .user-name { color: #e5e7eb; }
.dash-dark .user-email { color: #9ca3af; }
.dash-dark .user-id { color: #787b86; }
.dash-dark .status-label { color: #e5e7eb; }
.dash-dark .plan-badge { background: rgba(34,197,94,0.1); border-color: rgba(34,197,94,0.3); }
.dash-dark .detail-label { color: #787b86; }
.dash-dark .detail-value { color: #d1d4dc; }
.dash-dark .sub-empty p { color: #9ca3af; }
.dash-dark .empty-state { color: #787b86; }
.dash-dark .btn-primary { background: #e5e7eb; color: #0c1018; }
.dash-dark .btn-primary:hover { background: #22c55e; color: #fff; }
.dash-dark .action-card {
  background: #121826;
  border-color: rgba(55, 59, 75, 0.5);
  color: #d1d4dc;
}
.dash-dark .action-card:hover {
  border-color: #22c55e;
  background: rgba(34,197,94,0.08);
  box-shadow: 0 4px 12px rgba(0,0,0,0.3);
}
.dash-dark .action-card svg { color: #9ca3af; }
.dash-dark .action-card:hover svg { color: #22c55e; }
.dash-dark .broker-dropdown-selected { background: #1a2035; border-color: rgba(55, 59, 75, 0.5); }
.dash-dark .broker-dropdown.open .broker-dropdown-selected { border-color: #f5a623; }
.dash-dark .broker-dropdown-selected:hover { border-color: rgba(55, 59, 75, 0.8); }
.dash-dark .broker-dropdown-name { color: #e5e7eb; }
.dash-dark .broker-dropdown-placeholder { color: #6b7280; }
.dash-dark .broker-dropdown-arrow { color: #6b7280; }
.dash-dark .broker-dropdown-menu { background: #1a2035; border-color: rgba(55, 59, 75, 0.5); box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3); }
.dash-dark .broker-dropdown-search { background: #141824; border-bottom-color: rgba(55, 59, 75, 0.5); color: #e5e7eb; }
.dash-dark .broker-dropdown-option:hover { background: rgba(255, 255, 255, 0.04); }
.dash-dark .broker-dropdown-option.active { background: rgba(245, 166, 35, 0.06); }
.dash-dark .broker-dropdown-option-name { color: #e5e7eb; }
.dash-dark .broker-dropdown-option-desc { color: #6b7280; }
.dash-dark .broker-detail-panel { background: #141824; border-color: rgba(55, 59, 75, 0.5); }
.dash-dark .broker-detail-name { color: #e5e7eb; }
.dash-dark .broker-detail-features { color: #9ca3af; }
.dash-dark .broker-detail-badge { background: #1a2035; color: #787b86; }
.dash-dark .broker-detail-badge.connected { background: rgba(34,197,94,0.1); color: #22c55e; }
.dash-dark .broker-detail-badge.expired { background: rgba(245,158,11,0.1); color: #fbbf24; }
.dash-dark .broker-detail-badge.disabled { background: rgba(220,38,38,0.1); color: #f87171; }
.dash-dark .broker-disabled-msg { color: #f87171; }
.dash-dark .broker-title { color: #e5e7eb; }
.dash-dark .broker-desc { color: #9ca3af; }
.dash-dark .broker-selector-label { color: #9ca3af; }
.dash-dark .broker-empty-state { color: #6b7280; }
.dash-dark .broker-empty-state svg { color: rgba(55, 59, 75, 0.8); }
.dash-dark .btn-broker-secondary { color: #9ca3af; border-color: rgba(55, 59, 75, 0.5); }
.dash-dark .btn-broker-secondary:hover { background: rgba(255, 255, 255, 0.04); color: #e5e7eb; }
.dash-dark .ai-description { color: #9ca3af; }
.dash-dark .ai-features { color: #9ca3af; }
.dash-dark .label-text { color: #e5e7eb; }
.dash-dark .form-label-text { color: #9ca3af; }
.dash-dark .form-input {
  background: #1a2035;
  border-color: rgba(55, 59, 75, 0.5);
  color: #d1d4dc;
}
.dash-dark .form-input:focus { border-color: #22c55e; }
.dash-dark .input-btn {
  background: #1a2035;
  border-color: rgba(55, 59, 75, 0.5);
  color: #9ca3af;
}
.dash-dark .input-btn:hover { border-color: #22c55e; color: #22c55e; }
.dash-dark .form-hint { color: #787b86; }
.dash-dark .btn-secondary {
  border-color: rgba(55, 59, 75, 0.5);
  color: #9ca3af;
}
.dash-dark .btn-secondary:hover { border-color: #f87171; color: #f87171; }
.dash-dark .ai-info { background: rgba(34,197,94,0.08); border-color: rgba(34,197,94,0.3); }
.dash-dark .info-item { color: #86efac; }
.dash-dark .usage-title { color: #e5e7eb; }
.dash-dark .usage-step { color: #9ca3af; }
.dash-dark .step-number { background: #22c55e; }
.dash-dark .spinner { border-color: rgba(55, 59, 75, 0.5); border-top-color: #22c55e; }
.dash-dark .dash-loading p { color: #9ca3af; }
.dash-dark .modal-content-dash { background: #121826; }
.dash-dark .modal-header-dash { border-bottom-color: rgba(55, 59, 75, 0.4); }
.dash-dark .modal-header-dash h3 { color: #e5e7eb; }
.dash-dark .close-btn { color: #9ca3af; }
.dash-dark .close-btn:hover { color: #e5e7eb; }
.dash-dark .badge-green { background: rgba(34,197,94,0.15); }
.dash-dark .badge-muted { background: #1a2035; color: #787b86; }
.dash-dark .dash-footer { border-top-color: rgba(55, 59, 75, 0.4); color: #787b86; }

/* Dark theme for trial card */
.dash-dark .trial-card {
  background: linear-gradient(135deg, rgba(34,197,94,0.1) 0%, rgba(34,197,94,0.05) 100%);
  border-color: rgba(34,197,94,0.3);
}
.dash-dark .trial-info h4 { color: #86efac; }
.dash-dark .trial-info p { color: #4ade80; }
.dash-dark .btn-trial { background: #22c55e; }
.dash-dark .btn-trial:hover:not(:disabled) { background: #16a34a; }
.dash-dark .trial-error { color: #f87171; }

/* ── Dark mode: New Overview Redesign ── */
.dash-dark .ov-hero {
  background: linear-gradient(135deg, #0f1e13 0%, #0d1a10 60%, #101c14 100%);
  border-color: rgba(34, 197, 94, 0.15);
}
.dash-dark .ov-hero-inactive {
  background: linear-gradient(135deg, #0f1520 0%, #121826 100%);
  border-color: rgba(55, 59, 75, 0.4);
}
.dash-dark .ov-hero-name { color: #e5e7eb; }
.dash-dark .ov-hero-greeting { color: #787b86; }
.dash-dark .ov-hero-email { color: #787b86; }
.dash-dark .pill-active { background: rgba(34,197,94,0.15); color: #86efac; border-color: rgba(34,197,94,0.3); }
.dash-dark .pill-inactive { background: rgba(55,59,75,0.3); color: #9ca3af; border-color: rgba(55,59,75,0.4); }
.dash-dark .ov-stat-card { background: #121826; border-color: rgba(55, 59, 75, 0.5); }
.dash-dark .ov-stat-clickable:hover { border-color: rgba(34, 197, 94, 0.3); box-shadow: 0 4px 14px rgba(0,0,0,0.25); }
.dash-dark .ov-icon-sub { background: rgba(22,163,74,0.12); }
.dash-dark .ov-icon-days { background: rgba(37,99,235,0.12); }
.dash-dark .ov-icon-warn { background: rgba(217,119,6,0.12); }
.dash-dark .ov-icon-ai { background: rgba(147,51,234,0.12); }
.dash-dark .ov-icon-pay { background: rgba(234,88,12,0.12); }
.dash-dark .ov-icon-broker { background: rgba(55,59,75,0.3); color: #9ca3af; }
.dash-dark .ov-icon-broker-live { background: rgba(22,163,74,0.15); }
.dash-dark .ov-stat-label { color: #787b86; }
.dash-dark .ov-stat-val { color: #e5e7eb; }
.dash-dark .ov-stat-sub { color: #787b86; }
.dash-dark .ov-credits-mini-bar { background: #1e2a3a; }
.dash-dark .ov-section-label { color: #787b86; }
.dash-dark .ov-qa-btn { background: #121826; border-color: rgba(55,59,75,0.5); color: #d1d4dc; }
.dash-dark .ov-qa-btn:hover { border-color: #22c55e; background: rgba(34,197,94,0.08); color: #86efac; }
.dash-dark .ov-qa-icon-chart { background: rgba(22,163,74,0.12); }
.dash-dark .ov-qa-icon-bots { background: rgba(37,99,235,0.12); }
.dash-dark .ov-qa-icon-broker { background: rgba(234,88,12,0.12); }
.dash-dark .ov-qa-icon-plan { background: rgba(147,51,234,0.12); }
.dash-dark .ov-qa-icon-theme { background: rgba(55,59,75,0.3); color: #9ca3af; }
.dash-dark .ov-trial-banner { background: linear-gradient(135deg, #0a0f14 0%, #0c1810 100%); }
.dash-dark .ov-trial-title { color: #e5e7eb; }
.dash-dark .ov-trial-desc { color: #787b86; }

/* Dark responsive */
@media (max-width: 768px) {
  .dash-dark .dash-sidebar { border-bottom-color: rgba(55, 59, 75, 0.5); }
}

/* ── Trial Badge ── */
.trial-badge {
  background: #f5a623;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 4px;
  margin-left: 8px;
  letter-spacing: 0.5px;
}

.trial-badge-lg {
  background: #f5a623;
  color: #fff;
  font-size: 12px;
  padding: 6px 14px;
  border-radius: 6px;
}

/* ── AI Credits Section ── */
.ai-credits-section {
  margin: 24px 0;
  padding: 20px;
  background: #f9fafb;
  border-radius: 10px;
  border: 1px solid #e8e5de;
}

.credits-title {
  margin: 0 0 16px 0;
  font-size: 14px;
  font-weight: 600;
  color: #0f1923;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.credits-display {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.credits-main {
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.credits-remaining {
  font-size: 32px;
  font-weight: 700;
  color: #16a34a;
  font-family: 'DM Mono', monospace;
}

.credits-total {
  font-size: 16px;
  color: #8a9aaa;
  font-family: 'DM Mono', monospace;
}

.credits-label {
  font-size: 13px;
  color: #8a9aaa;
  margin-left: 4px;
}

.credits-bar-container {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.credits-bar-bg {
  height: 8px;
  background: #e8e5de;
  border-radius: 4px;
  overflow: hidden;
}

.credits-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #16a34a, #22c55e);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.credits-percent {
  font-size: 12px;
  color: #8a9aaa;
}

/* Credits bar in overview card */
.credits-bar {
  margin-top: 12px;
  height: 6px;
  background: #e8e5de;
  border-radius: 3px;
  overflow: hidden;
}

.credits-bar .credits-bar-fill {
  border-radius: 3px;
}

/* Dark mode styles */
.dash-dark .trial-badge,
.dash-dark .trial-badge-lg { background: #f5a623; }

.dash-dark .ai-credits-section {
  background: #1a2035;
  border-color: rgba(55, 59, 75, 0.5);
}

.dash-dark .credits-title { color: #e5e7eb; }
.dash-dark .credits-remaining { color: #22c55e; }
.dash-dark .credits-total { color: #787b86; }
.dash-dark .credits-label { color: #787b86; }
.dash-dark .credits-bar-bg { background: #2a3142; }
.dash-dark .credits-bar { background: #2a3142; }
.dash-dark .credits-percent { color: #787b86; }

.dash-dark .credits-remaining { color: #22c55e; }
.dash-dark .credits-total { color: #787b86; }
.dash-dark .credits-label { color: #787b86; }
.dash-dark .credits-bar-bg { background: #2a3142; }
.dash-dark .credits-bar { background: #2a3142; }

/* ══════════════════════════════════════════
   USER PROFILE DROPDOWN
   ══════════════════════════════════════════ */
.user-profile-dropdown {
  position: relative;
  margin-left: 8px;
}

.user-profile-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 12px;
  border: 1.5px solid #e8e5de;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  transition: all 0.15s;
}

.dash-dark .user-profile-btn {
  border-color: rgba(55, 59, 75, 0.6);
}

.user-profile-btn:hover {
  border-color: #22c55e;
  background: rgba(34, 197, 94, 0.05);
}

.dash-dark .user-profile-btn:hover {
  border-color: #22c55e;
  background: rgba(34, 197, 94, 0.1);
}

.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f3fa;
}

.dash-dark .user-avatar {
  background: #1e2436;
}

.user-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  color: #6b7280;
}

.dash-dark .avatar-placeholder {
  color: #9ca3af;
}

.user-name {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dash-dark .user-name {
  color: #d1d4dc;
}

.dropdown-arrow {
  color: #6b7280;
  transition: transform 0.2s;
}

.dash-dark .dropdown-arrow {
  color: #9ca3af;
}

.user-profile-dropdown.active .dropdown-arrow {
  transform: rotate(180deg);
}

.profile-dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 200px;
  background: #ffffff;
  border: 1px solid #e8e5de;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  opacity: 0;
  visibility: hidden;
  transform: translateY(-8px);
  transition: all 0.2s;
  z-index: 1000;
}

.dash-dark .profile-dropdown-menu {
  background: #1a2035;
  border-color: rgba(55, 59, 75, 0.6);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.user-profile-dropdown.active .profile-dropdown-menu {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  color: #374151;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
}

.dash-dark .menu-item {
  color: #d1d4dc;
}

.menu-item:hover {
  background: #f8f9fa;
  color: #22c55e;
}

.dash-dark .menu-item:hover {
  background: rgba(34, 197, 94, 0.1);
  color: #22c55e;
}

.menu-item:first-child {
  border-radius: 8px 8px 0 0;
}

.menu-item:last-child {
  border-radius: 0 0 8px 8px;
}

.menu-divider {
  height: 1px;
  background: #e8e5de;
  margin: 4px 0;
}

.dash-dark .menu-divider {
  background: rgba(55, 59, 75, 0.6);
}

.logout-item {
  color: #dc2626;
}

.dash-dark .logout-item {
  color: #f87171;
}

.logout-item:hover {
  background: rgba(220, 38, 38, 0.1);
  color: #dc2626;
}

.dash-dark .logout-item:hover {
  background: rgba(239, 68, 68, 0.1);
  color: #f87171;
}

/* Admin link styling */
.admin-link {
  color: #8b5cf6 !important;
  font-weight: 500;
  border: 1px solid #8b5cf6;
  border-radius: 6px;
  padding: 6px 12px;
  margin: 0 4px;
  transition: all 0.2s ease;
}

.admin-link:hover {
  background: #8b5cf6 !important;
  color: white !important;
}

.dash-dark .admin-link {
  color: #a78bfa !important;
  border-color: #a78bfa;
}

.dash-dark .admin-link:hover {
  background: #a78bfa !important;
  color: white !important;
}

.dash-dark .credits-percent { color: #787b86; }
</style>
